// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

// material ui
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';

// api
import { useGetV2AlarmsByIdQuery } from '@/api/generated/recalc/endpoints/v2/alarms';

// feature-components
import AlarmTypeVisualization from '@/features/alarm-hub/components/AlarmTypeVisualization';
import { toLegacyAlarmShape } from '@/features/alarm-hub/components/alarmTypeVisualizationShared.ts';

type EnergyXplorerAlarmDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
  alarmId: number | null;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
};

export default function EnergyXplorerAlarmDetailDialog({
  open,
  onClose,
  accountId,
  alarmId,
  isFavorite = false,
  onToggleFavorite,
}: EnergyXplorerAlarmDetailDialogProps) {
  const { t } = useTranslation(['alarms', 'energy-analysis']);
  const parsedAlarmId = Number(alarmId);
  const parsedAccountId = Number(accountId);
  const hasValidAlarmId = Number.isFinite(parsedAlarmId);
  const hasValidAccountId = Number.isFinite(parsedAccountId);

  const { currentData: alarmResponse } = useGetV2AlarmsByIdQuery(
    {
      id: parsedAlarmId,
      accountIds: hasValidAccountId ? [parsedAccountId] : [],
    },
    {
      skip: !open || !hasValidAlarmId || !hasValidAccountId,
    },
  );

  const alarm = toLegacyAlarmShape(alarmResponse);

  const favoriteLabel = isFavorite
    ? t('portfolio.deepdive_alarms.actions.remove_favorite', {
        ns: 'energy-analysis',
      })
    : t('portfolio.deepdive_alarms.actions.add_favorite', {
        ns: 'energy-analysis',
      });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'right',
        }}
      >
        {onToggleFavorite ? (
          <Tooltip title={favoriteLabel}>
            <IconButton
              aria-label={favoriteLabel}
              onClick={onToggleFavorite}
              //align to the right of dialogtitle without absolute
              sx={{ justifyItems: 'right' }}
            >
              {isFavorite ? (
                <StarRoundedIcon color="warning" />
              ) : (
                <StarBorderRoundedIcon />
              )}
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton aria-label={t('form.cancel')} onClick={onClose} sx={{}}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {alarmResponse ? (
            <AlarmTypeVisualization
              key={alarmResponse.alarmId ?? parsedAlarmId}
              alarm={alarm}
            />
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
